import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/browser';

import Error from 'components/Error';

interface Props {
  children?: ReactNode;
  // Shown instead of the whole page error, when only a part of the page has failed
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorInfo?: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    errorInfo: '',
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, errorInfo: error.message };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Frontend error:', error, errorInfo);
    // React does not pass an error caught by a boundary to the global handlers, so Sentry gets it only here
    Sentry.withScope((scope) => {
      scope.setExtra('componentStack', errorInfo.componentStack);
      Sentry.captureException(error);
    });
    this.setState({ errorInfo: errorInfo.componentStack });
  }

  public render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <Error trace={this.state.errorInfo} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
